/**
 * Rehydration Manager - Strict F5 Refresh Pipeline
 * 
 * Enforces the following blocking sequence on app load when an active session exists:
 * 1. Await POST /api/v1/auth/refresh (utilizing httpOnly cookie)
 * 2. Hydrate useAuthStore with new access token
 * 3. Initialize Socket.io connection
 * 4. Emit reconnect:attempt { match_id }
 * 5. Block rendering of actual board until new game:state payload received from server
 * 
 * This prevents rendering GameBoardScreen with stale/empty data.
 */

import { apiClient } from '../services/rest/apiClient';
import { useAuthStore, getCurrentAccessToken, setCurrentAccessToken, clearCurrentAuth } from '../stores/useAuthStore';
import { initializeSocket, connectSocket, typedEmit, getRawSocket } from '../services/socket/gameSocket';
import { useGameStore } from '../stores/useGameStore';
import { useMatchmakingStore } from '../stores/useMatchmakingStore';
import { WS_MOUNT_PATH, API_PREFIX } from '../types/common';

/**
 * State of the rehydration pipeline
 */
export type RehydrationStatus = 
  | 'idle'
  | 'authenticating'
  | 'connecting'
  | 'awaiting_game_state'
  | 'ready'
  | 'error';

export interface RehydrationState {
  status: RehydrationStatus;
  matchId: string | null;
  progress: number;
  error: string | null;
}

/**
 * Singleton rehydration manager
 */
class RehydrationManager {
  private static instance: RehydrationManager;
  private state: RehydrationState = {
    status: 'idle',
    matchId: null,
    progress: 0,
    error: null,
  };
  private listeners: Set<(state: RehydrationState) => void> = new Set();
  private abortController: AbortController | null = null;

  static getInstance(): RehydrationManager {
    if (!RehydrationManager.instance) {
      RehydrationManager.instance = new RehydrationManager();
    }
    return RehydrationManager.instance;
  }

  private notify() {
    this.listeners.forEach(fn => fn(this.state));
  }

  subscribe(fn: (state: RehydrationState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  getState(): RehydrationState {
    return this.state;
  }

  private setStatus(status: RehydrationStatus, partial?: Partial<RehydrationState>) {
    this.state = { ...this.state, status, ...partial };
    this.notify();
  }

  private setProgress(progress: number) {
    this.state = { ...this.state, progress };
    this.notify();
  }

  /**
   * Execute the strict rehydration pipeline
   * Must be called once on app startup
   */
  async executePipeline(): Promise<void> {
    if (this.state.status !== 'idle') {
      console.warn('[RehydrationManager] Pipeline already executed or in progress');
      return;
    }

    this.abortController = new AbortController();
    
    try {
      // Step 1: Check if we have a persisted user (indicating previous session)
      const authStore = useAuthStore.getState();
      const persistedUser = authStore.user;
      
      if (!persistedUser) {
        console.log('[RehydrationManager] No persisted user, skipping rehydration pipeline');
        this.setStatus('ready', { progress: 100 });
        return;
      }

      console.log('[RehydrationManager] Starting strict rehydration pipeline...');
      
      // Step 2: Refresh token via httpOnly cookie
      await this.refreshToken();
      
      // Step 3: Initialize and connect socket
      await this.connectSocket();
      
      // Step 4: Check if we have an active match to reconnect to
      const gameStore = useGameStore.getState();
      const activeMatchId = gameStore.matchId;
      
      if (activeMatchId) {
        await this.reconnectMatch(activeMatchId);
      }
      
      this.setStatus('ready', { progress: 100 });
      console.log('[RehydrationManager] Pipeline complete');
      
    } catch (err) {
      this.setStatus('error', { 
        error: err instanceof Error ? err.message : 'Rehydration failed',
        progress: 0
      });
      console.error('[RehydrationManager] Pipeline failed:', err);
      
      // On failure, clear auth and allow normal login flow
      clearCurrentAuth();
      useAuthStore.getState().clearAuth();
      useMatchmakingStore.getState().reset();
      useGameStore.getState().resetMatch();
      throw err;
    }
  }

  private async refreshToken(): Promise<void> {
    this.setStatus('authenticating', { progress: 20 });

    try {
      // Call refresh endpoint - cookie is automatically sent by browser
      const response = await apiClient.post(`${API_PREFIX}/auth/refresh`);
      
      const { access_token, refresh_token, expires_in, token_type = 'Bearer' } = response.data.tokens;
      
      // Update in-memory tokens and store
      setCurrentAccessToken(access_token, expires_in);
      
      // Also update the store directly
      useAuthStore.getState().setTokens({
        access_token,
        refresh_token,
        expires_in,
        token_type
      });
      
      this.setProgress(40);
      console.log('[RehydrationManager] Token refreshed successfully');
    } catch (err) {
      console.error('[RehydrationManager] Token refresh failed:', err);
      throw new Error('Session expired. Please log in again.');
    }
  }

  private async connectSocket(): Promise<void> {
    this.setStatus('connecting', { progress: 50 });

    // Get the new access token
    const token = getCurrentAccessToken();
    if (!token) {
      throw new Error('No access token after refresh');
    }

    // Initialize socket with auth
    const wsUrl = `${window.location.origin}${WS_MOUNT_PATH}`;
    initializeSocket({
      url: wsUrl,
      auth: { token },
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ['websocket', 'polling'],
    });

    // Connect and wait for connection
    await connectSocket();
    
    // Socket is now connected, bind controller
    const { autoBindOnConnect } = await import('../services/socket/socketController');
    autoBindOnConnect();
    
    this.setProgress(60);
    console.log('[RehydrationManager] Socket connected and controller bound');
  }

  private async reconnectMatch(matchId: string): Promise<void> {
    this.setStatus('awaiting_game_state', { matchId, progress: 70 });

    // Emit reconnect attempt
    typedEmit('reconnect:attempt', { match_id: matchId });
    
    // Wait for game:state to arrive (max 10 seconds)
    await this.waitForGameState(matchId);
  }

  private waitForGameState(_matchId: string, timeoutMs = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = getRawSocket();
      if (!socket) {
        reject(new Error('Socket not available'));
        return;
      }

      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Timeout waiting for game state after reconnect'));
      }, timeoutMs);

      const handler = (payload: any) => {
        // Check if this game state is for our match
        if (payload && typeof payload === 'object' && 'turn' in payload) {
          cleanup();
          console.log('[RehydrationManager] Received game:state after reconnect');
          resolve();
        }
      };

      const cleanup = () => {
        clearTimeout(timeout);
        socket.off('game:state', handler);
      };

      socket.on('game:state', handler);
    });
  }

  /**
   * Reset manager for new session
   */
  reset(): void {
    this.state = {
      status: 'idle',
      matchId: null,
      progress: 0,
      error: null,
    };
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.notify();
  }
}

/**
 * Initialize rehydration pipeline - call once in App.tsx
 */
export async function initializeRehydration(): Promise<void> {
  const manager = RehydrationManager.getInstance();
  await manager.executePipeline();
}

export { RehydrationManager };